import {
  Component,
  effect,
  inject,
  Injector,
  input,
  model,
  output,
  Type,
  untracked,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import {
  AutomationRuleType,
  IAutomationRule,
} from '@kinet-open-network/shared/data-access/model';
import { FormlyModule } from '@ngx-formly/core';
import { ButtonModule } from 'primeng/button';
import { Subject, takeUntil } from 'rxjs';
import { AutomationConditionService } from './automation-condition.service';
import { RULE_TOKEN, RuleComponent } from './rules/abstract-rule.component';

@Component({
  selector: 'kon-automation-condition-rule-container',
  imports: [FormlyModule, ButtonModule],
  template: `
    <div class="flex items-start gap-2">
      <div class="grow">
        <ng-template #vcr />
      </div>

      <p-button
        type="button"
        icon="pi pi-times"
        pTooltip="Zmazať podmienku"
        [text]="true"
        [severity]="'danger'"
        [size]="'small'"
        [rounded]="true"
        (click)="removeRule()"
      ></p-button>
    </div>
  `,
  styles: ``,
})
export class AutomationConditionRuleContainerComponent {
  readonly #conditionService = inject(AutomationConditionService);
  readonly #recreateStream = new Subject<void>();

  readonly rule = model<Partial<IAutomationRule>>(
    this.#conditionService.createRule(),
  );
  readonly vcr = viewChild('vcr', { read: ViewContainerRef });
  readonly ruleRemove = output<void>();
  readonly enabledRules = input<AutomationRuleType[]>([]);

  private ruleComponentInstance: RuleComponent | null = null;

  constructor() {
    effect(() => {
      const type = this.rule().rule;
      const vcr = this.vcr();

      if (!vcr) {
        return;
      }

      if (
        this.ruleComponentInstance &&
        type === this.ruleComponentInstance.rule.rule
      ) {
        return;
      }

      const component = this.#conditionService.getComponentForType(type);

      untracked(() => {
        this.recreateComponent(component, vcr);
      });
    });
  }

  handleModelChange(data: IAutomationRule) {
    const currentRule = this.rule();

    if (data.rule !== currentRule.rule) {
      this.rule.update((current) => ({
        ...current,
        ...data,
        operator: undefined,
        value: null,
      }));
    } else {
      this.rule.set(data);
    }
  }

  removeRule() {
    this.ruleRemove.emit();
  }

  private recreateComponent(
    componentType: Type<RuleComponent>,
    vcr: ViewContainerRef,
  ) {
    vcr.clear();
    this.#recreateStream.next();
    this.ruleComponentInstance = null;

    const injector = Injector.create({
      providers: [
        {
          provide: RULE_TOKEN,
          useValue: this.rule(),
        },
      ],
      parent: vcr.injector,
    });

    const componentRef = vcr.createComponent(componentType, { injector });

    const componentInstance = componentRef.instance;

    outputToObservable(componentInstance.ruleChange)
      .pipe(takeUntil(this.#recreateStream))
      .subscribe((rule) => {
        this.handleModelChange(rule);
      });

    this.ruleComponentInstance = componentInstance;

    componentRef.changeDetectorRef.detectChanges();
  }
}
